import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { formatDate } from '../../utils/format';
import { STATUS_LABELS, STATUS_TONES, KINDS, KIND_LABELS, kindOf, surveyRequiredByDefault } from '../../utils/siteSurveys';

// The surveys on one opportunity (SOW detail's Surveys tab) or one bid (the
// bid record's Surveys tab), with Request a survey. A bid's surveys also show
// on the opportunity its win raised. On an opportunity the panel also carries
// whether a survey is needed at all.

const input = 'w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] bg-white';
const label = 'block text-[9px] font-semibold text-gray-500 uppercase mb-0.5';
const btnNavy = 'px-3 py-1.5 text-[10px] font-bold rounded border bg-navy text-white border-navy hover:bg-navy/80 disabled:opacity-50';
const btnPlain = 'px-3 py-1.5 text-[10px] font-bold rounded border bg-white text-gray-700 border-gray-200 hover:border-gold disabled:opacity-50';

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(new Error(`Could not read ${file.name}`));
  r.readAsDataURL(file);
});
const place = (desk, area) => [desk, area].filter(Boolean).join(' · ');

// The moves a move list holds, as the survey will start with them, so the
// person requesting can see the sheet was read right before raising it.
function MoveListPreview({ list, onRemove }) {
  return (
    <div className="mt-2 border border-gray-200 rounded">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <span className="text-[10px] text-gray-700"><b>{list.moves.length} {list.moves.length === 1 ? 'move' : 'moves'}</b> read from {list.file_name}</span>
        <button type="button" onClick={onRemove} className="text-[10px] text-gray-500 hover:text-navy underline">Remove</button>
      </div>
      <div className="max-h-56 overflow-auto">
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 bg-white">
            <tr className="text-left text-gray-500">
              {['Move', 'Who', 'Team', 'From', 'To'].map(h => <th key={h} className="px-2 py-1 font-semibold border-b border-gray-200">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.moves.map(m => (
              <tr key={m.move_ref} className="border-b border-gray-100 align-top">
                <td className="px-2 py-1 font-mono text-gray-500 whitespace-nowrap">{m.move_ref}</td>
                <td className="px-2 py-1 text-gray-700">{m.user_name || ''}</td>
                <td className="px-2 py-1 text-gray-700">{m.team || ''}</td>
                <td className="px-2 py-1 text-gray-700">{place(m.from_desk, m.from_area)}</td>
                <td className="px-2 py-1 text-gray-700">{place(m.to_desk, m.to_area)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.notes.length > 0 && (
        <ul className="list-disc pl-6 pr-2 py-1.5 text-[10px] text-amber-700 space-y-0.5 border-t border-gray-200">{list.notes.map(n => <li key={n}>{n}</li>)}</ul>
      )}
    </div>
  );
}

function RequestModal({ sowId, bidId, people, onClose, onMade }) {
  const [form, setForm] = useState({ survey_type: 'room', title: '', location: '', needed_by: '', assigned_to: '', visit_date: '', paper_only: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A desk move's move list: the file, and the moves read from it.
  const [list, setList] = useState(null);
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const chooseList = async (file) => {
    setList(null); setListError('');
    if (!file) return;
    setListBusy(true);
    try {
      const upload = { filename: file.name, content_type: file.type, data: await readFile(file) };
      const read = await api.post('surveys/move-list', upload);
      setList({ ...read, upload });
    } catch (e) { setListError(e.message); } finally { setListBusy(false); }
  };
  const save = async () => {
    if (!form.title.trim()) { setError('Name the room or area to survey.'); return; }
    setBusy(true); setError('');
    try {
      const body = {
        ...(sowId ? { sow_id: sowId } : { bid_id: bidId }),
        survey_type: form.survey_type, title: form.title, needed_by: form.needed_by || null, assigned_to: form.assigned_to || null,
        visit_date: form.visit_date || null, paper_only: form.paper_only,
        ...(form.location.trim() ? { location: form.location } : {}),
        ...(form.survey_type === 'moves' && list ? { move_list: list.upload } : {}),
      };
      onMade(await api.post('surveys', body));
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal title="Request a site survey" onClose={onClose} wide={form.survey_type === 'moves'}
      footer={<>
        <button onClick={onClose} className={btnPlain}>Close</button>
        <button onClick={save} disabled={busy || listBusy} className={btnNavy}>{form.survey_type === 'moves' && list ? `Request survey with ${list.moves.length} ${list.moves.length === 1 ? 'move' : 'moves'}` : 'Request survey'}</button>
      </>}>
      <div className="text-[10px] text-gray-500 mb-2">One survey per room or area. The opportunity's location, request and site contact are copied in, and the engineer can change them.</div>
      <div className="flex flex-wrap gap-1.5 mb-2" role="radiogroup" aria-label="Kind of survey">
        {KINDS.map(t => (
          <button key={t} type="button" role="radio" aria-checked={form.survey_type === t} onClick={() => set('survey_type', t)}
            className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border ${form.survey_type === t ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200'}`}>
            {KIND_LABELS[t]}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-gray-500 mb-2">
        {{
          room: 'A room survey is the nine questions of the AV / IT Site Survey, for a meeting room, office or other single space.',
          desks: 'A desk standardisation records each desk against the agreed standards and works out the kit to order, socket shortfalls and missing ports.',
          moves: 'A desk move records each person or desk that moves, from where to where, the ports and whether they are live at both ends, the kit that travels, and move day.',
        }[form.survey_type]}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="sm:col-span-2"><label className={label} htmlFor="rq-title">Room or area</label><input id="rq-title" className={input} value={form.title} onChange={e => set('title', e.target.value)} placeholder={form.survey_type === 'room' ? 'Sub Boardroom' : 'Cube 1st floor'} autoFocus /></div>
        <div className="sm:col-span-2"><label className={label} htmlFor="rq-loc">Location, if different from the opportunity's</label><input id="rq-loc" className={input} value={form.location} onChange={e => set('location', e.target.value)} placeholder="Building / floor / room / desks" /></div>
        <div>
          <label className={label} htmlFor="rq-who">Carried out by</label>
          <select id="rq-who" className={input} value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
            <option value="">Decide later</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div><label className={label} htmlFor="rq-visit">Visit date</label><input id="rq-visit" type="date" className={input} value={form.visit_date} onChange={e => set('visit_date', e.target.value)} /></div>
        <div><label className={label} htmlFor="rq-needed">Needed by</label><input id="rq-needed" type="date" className={input} value={form.needed_by} onChange={e => set('needed_by', e.target.value)} /></div>
        <label className="sm:col-span-2 flex items-start gap-2 text-[10px] text-gray-700">
          <input type="checkbox" className="mt-0.5" checked={form.paper_only} onChange={e => set('paper_only', e.target.checked)} />
          <span><b>Paper only.</b> Phones and cameras are not allowed on this site, so the engineer uses the printed form.</span>
        </label>
        {form.survey_type === 'moves' && (
          <div data-tour="request-move-list" className="sm:col-span-2">
            <label className={label} htmlFor="rq-list">Move list (optional)</label>
            <div className="text-[10px] text-gray-500 mb-1">The client's list of who moves where, as an Excel file: their own layout, the Desk Moves template or the Ops form. The survey starts with these moves and keeps the file.</div>
            <input id="rq-list" type="file" accept=".xlsx" className="text-[11px]" onChange={e => chooseList(e.target.files?.[0] || null)} />
            {listBusy && <div className="text-[10px] text-gray-500 mt-1">Reading the list…</div>}
            {listError && <div role="alert" className="text-[10px] text-red-600 mt-1">{listError}</div>}
            {list && <MoveListPreview list={list} onRemove={() => { setList(null); const el = document.getElementById('rq-list'); if (el) el.value = ''; }} />}
          </div>
        )}
      </div>
      {form.assigned_to && form.visit_date && <div className="text-[10px] text-gray-500 mt-2">With who and when both set, the survey is raised as Booked.</div>}
      {error && <div role="alert" className="text-[10px] text-red-600 mt-2">{error}</div>}
    </Modal>
  );
}

// A survey filled in on the Word form or the desk workbook, read into a new
// survey here. What the reader could not place is shown in the dialog, beside
// the file the person just chose, with the way into the new survey.
function ImportModal({ sowId, bidId, onClose, onOpen }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  // A form Ops issued for a survey already in Ops goes back into that survey.
  const [owner, setOwner] = useState(null);
  const run = async () => {
    if (!file) { setError('Choose the completed Word form (.docx) or desk workbook (.xlsx).'); return; }
    setBusy(true); setError('');
    try {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error(`Could not read ${file.name}`));
        r.readAsDataURL(file);
      });
      const res = await api.post('surveys/import', { ...(sowId ? { sow_id: sowId } : { bid_id: bidId }), filename: file.name, content_type: file.type, data, title: title.trim() || undefined });
      setDone(res);
    } catch (e) { setError(e.message); setOwner(e.data?.survey_id || null); } finally { setBusy(false); }
  };
  return (
    <Modal title={done ? `Imported as ${done.survey.reference}` : 'Import a completed survey'} onClose={done ? () => onOpen(null) : onClose}
      footer={done ? <>
        <button onClick={() => onOpen(null)} className={btnPlain}>Close</button>
        <button onClick={() => onOpen(done.survey.id)} className={btnNavy}>Open {done.survey.reference}</button>
      </> : <>
        <button onClick={onClose} className={btnPlain}>Close</button>
        <button onClick={run} disabled={busy || !file} className={btnNavy}>{busy ? 'Reading…' : 'Import'}</button>
      </>}>
      {done ? (
        <div className="text-[11px] text-gray-700">
          <div className="mb-2">{done.survey.title} is now a {KIND_LABELS[kindOf(done.survey)].toLowerCase()} survey{kindOf(done.survey) === 'moves' ? ` with ${done.survey.moves.length} moves` : kindOf(done.survey) === 'desks' ? ` with ${done.survey.desks.length} desks` : ` with ${done.survey.lines.length} lines`}, In progress. The file is kept with it.</div>
          {done.notes.length ? (
            <>
              <div className="font-semibold text-amber-700 mb-1">Check these, which the file did not answer clearly:</div>
              <ul className="list-disc pl-4 text-[10px] text-amber-700 space-y-0.5">{done.notes.map(n => <li key={n}>{n}</li>)}</ul>
            </>
          ) : <div className="text-green-700">Everything in the file was read.</div>}
        </div>
      ) : (
        <>
          <div className="text-[10px] text-gray-500 mb-2">A survey done on a blank form from Ops (Blank forms on the Surveys page), or on an older copy of the room, desk standardisation or desk move form. A form downloaded from a survey already in Ops goes back with Upload completed form on that survey. Older .doc and .xls files need saving as .docx or .xlsx first.</div>
          <label className={label} htmlFor="im-file">File</label>
          <input id="im-file" type="file" accept=".docx,.xlsx" className="text-[11px] mb-2" onChange={e => { setFile(e.target.files?.[0] || null); setError(''); setOwner(null); }} />
          <label className={label} htmlFor="im-title">Room or area (optional)</label>
          <input id="im-title" className={input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Taken from the file when left blank" />
          {error && (
            <div role="alert" className="text-[10px] text-red-600 mt-2">
              {error}
              {owner && <button onClick={() => onOpen(owner)} className="ml-1 underline text-navy">Open it</button>}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default function SurveysPanel({ sowId, bid, readOnly = false }) {
  const { navigate } = useApp();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reqMsg, setReqMsg] = useState(null);

  const load = useCallback(() => {
    const get = sowId
      ? api.get(`surveys/opportunity/${sowId}`)
      : api.get('surveys', { bid_id: bid.id }).then(d => ({ surveys: d.surveys }));
    get.then(d => { setData(d); setError(''); }).catch(e => setError(e.message));
  }, [sowId, bid?.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('surveys', { status: 'open' }).then(d => setPeople(d.people || [])).catch(() => {}); }, []);

  const setRequired = async (value) => {
    setReqMsg(null);
    try {
      const r = await api.put(`surveys/opportunity/${sowId}/requirement`, { required: value });
      setData(d => ({ ...d, ...r }));
      setReqMsg({ tone: 'ok', text: 'Saved.' });
    } catch (e) { setReqMsg({ tone: 'error', text: e.message }); }
  };

  if (error) return <div className="text-[10px] text-red-600 py-4">{error}</div>;
  if (!data) return <div className="text-[10px] text-gray-400 py-4">Loading surveys…</div>;
  const live = data.surveys.filter(s => s.status !== 'cancelled');

  return (
    <div data-tour="surveys-panel" className="space-y-3">
      {sowId && (
        <div className="bg-white border border-gray-200 rounded p-3">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-navy">
            <b>Survey needed before issue?</b>
            {data.can_set_required ? (
              <select className="border border-gray-200 rounded px-2 py-1 text-[10px] bg-white" aria-label="Survey needed before issue?"
                value={data.required_set === null ? 'default' : data.required_set ? 'yes' : 'no'}
                onChange={e => setRequired(e.target.value === 'default' ? null : e.target.value === 'yes')}>
                <option value="default">{`Default for ${data.type || 'this type'} (${surveyRequiredByDefault(data.type) ? 'yes' : 'no'})`}</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : <span>{data.required ? 'Yes' : 'No'}</span>}
            {reqMsg && <span role="status" className={`text-[10px] ${reqMsg.tone === 'error' ? 'text-red-600' : 'text-green-700'}`}>{reqMsg.text}</span>}
          </div>
          {data.required && !live.length && <div className="text-[10px] text-amber-700 mt-1">This opportunity needs a survey and has none yet.</div>}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <div className="text-[11px] font-bold text-navy">Site surveys ({live.length})</div>
          {!readOnly && (
            <div className="flex gap-1.5">
              <button data-tour="import-survey" onClick={() => setImporting(true)} className={btnPlain}>Import a completed survey</button>
              <button data-tour="request-survey" onClick={() => setRequesting(true)} className={btnNavy}>+ Request a survey</button>
            </div>
          )}
        </div>
        {data.surveys.length === 0
          ? <div className="text-center py-6 text-[10px] text-gray-400">No surveys yet. Physical work such as desk moves, desk installs and meeting rooms is surveyed before it is priced.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
                  <tr>
                    <th className="text-left px-2 py-1.5">Ref</th><th className="text-left px-2 py-1.5">Room or area</th>
                    <th className="text-left px-2 py-1.5">Engineer</th><th className="text-left px-2 py-1.5">Visit</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.surveys.map(s => (
                    <tr key={s.id} onClick={() => navigate('surveys', s.id)} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                      <td className="px-2 py-1.5 font-mono text-gold font-bold">{s.reference}</td>
                      <td className="px-2 py-1.5 text-navy">
                        {s.title}
                        {kindOf(s) !== 'room' && <span className="ml-1 px-1 rounded text-[8px] font-bold bg-blue-50 text-blue-700">{kindOf(s) === 'moves' ? 'Desk move' : 'Desks'}</span>}
                        {sowId && s.bid_reference && !s.sow_id && <span className="ml-1 text-[8px] text-gray-400">from bid {s.bid_reference}</span>}
                        {!!s.paper_only && <span className="ml-1 px-1 rounded text-[8px] font-bold bg-amber-50 text-amber-700">Paper</span>}
                      </td>
                      <td className="px-2 py-1.5 text-gray-600">{s.assigned_name || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{s.visit_date ? formatDate(s.visit_date) : '-'}</td>
                      <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${STATUS_TONES[s.status]}`}>{STATUS_LABELS[s.status]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {importing && (
        <ImportModal sowId={sowId} bidId={bid?.id} onClose={() => setImporting(false)}
          onOpen={(id) => { setImporting(false); if (id) navigate('surveys', id); else load(); }} />
      )}
      {requesting && (
        <RequestModal sowId={sowId} bidId={bid?.id} people={people} onClose={() => setRequesting(false)}
          onMade={(s) => { setRequesting(false); navigate('surveys', s.id); }} />
      )}
    </div>
  );
}
